// SchedulePage: Event schedule
import React, { useState, useEffect } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Button } from '@mui/material';
import BookingModal from '../components/BookingModal';
import { getCMS } from '../services/cms';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';

function SchedulePage() {
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [raw, setRaw] = useState(null);
  const [bgMap, setBgMap] = useState({});
  const theme = useTheme();
  const mountImg = 'https://i.postimg.cc/14WyB7nZ/mount-kilimanjaro.png';
  const drumImg = 'https://i.postimg.cc/McqQY9H1/drum.png';
  const faceImg = 'https://i.postimg.cc/WDTk75z5/maasai-Face.png';
  const decoImages = [mountImg, drumImg, faceImg];

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get('/api/cms')
      .then(({ data }) => {
        setRaw(data);
        const scheduleItems = (data || [])
          .filter(i => i.type === 'schedule' && Array.isArray(i.contentJSON?.items) && i.contentJSON.items.length > 0)
          .flatMap(i => i.contentJSON.items.map(item => ({ ...item })));
        const sorted = scheduleItems.slice().sort((a, b) => {
          if (!a.time || !b.time) return 0;
          const parse = t => {
            const d = new Date('1970-01-01T' + t.replace(/(\d{1,2}):(\d{2}) ?([AP]M)?/, (m, h, min, ap) => `${h.padStart(2, '0')}:${min}:00${ap ? (ap === 'PM' && h !== '12' ? ' PM' : ' AM') : ''}`));
            return d.getTime();
          };
          return parse(a.time) - parse(b.time);
        });
        setSchedule(sorted);
      })
      .catch(e => setError(e.message || 'Failed to load schedule'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let mounted = true;
    getCMS().then(items => {
      if (!mounted) return;
      const landing = (items || []).find(i => i.type === 'landing');
      setBgMap(landing?.contentJSON?.backgrounds || {});
    }).catch(() => {}).finally(() => { mounted = false; });
  }, []);

  return (
    <PublicLayout>
      <Box sx={{ position: 'relative', width: '100%', minHeight: '56vh', py: { xs: 4, md: 6 }, overflow: 'hidden' }}>
        {bgMap.schedule && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgMap.schedule})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', zIndex: 0 }} />
        )}
        {bgMap.schedule && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.12)', zIndex: 0 }} />
        )}
        <Box sx={{ maxWidth: 1000, mx: 'auto', position: 'relative', zIndex: 1, p: 2 }}>
        {/* Decorative mount image: sit near the schedule belt and align to the side */}
        <Box component="img" src={mountImg} alt="mount" sx={{
          position: 'absolute',
          right: { xs: 8, md: -24 },
          top: { xs: -8, md: -28 },
          width: { xs: 88, md: 300 },
          opacity: 0.12,
          pointerEvents: 'none',
          zIndex: 0,
          transform: { xs: 'none', md: 'translateY(0)' }
        }} />
        <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, boxShadow: 4, p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom color="primary">Schedule</Typography>
        {loading ? <Typography>Loading...</Typography>
        : error ? <Typography color="error">{error}</Typography>
        : schedule.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, position: 'relative', zIndex: 1 }}>
            {schedule.map((item, idx) => {
              const decImg = decoImages[idx % decoImages.length];
              const bgPos = idx % 2 === 0 ? 'right 20px center' : 'left 20px center';
              return (
              <Box
                key={idx}
                sx={theme => ({
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(6,8,12,0.48)' : 'rgba(255,255,255,0.72)',
                  borderRadius: 3,
                  boxShadow: theme.palette.mode === 'dark' ? '0 6px 24px rgba(0,0,0,0.6)' : '0 8px 20px rgba(16,24,40,0.06)',
                  p: { xs: 1.5, sm: 2 },
                  gap: { xs: 1.5, sm: 3 },
                  borderLeft: { xs: 'none', sm: '6px solid' },
                  borderTop: { xs: '6px solid', sm: 'none' },
                  borderColor: idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main,
                  transition: 'transform 0.18s, box-shadow 0.18s',
                  minHeight: 72,
                  width: '100%',
                  maxWidth: '100%',
                  backdropFilter: 'blur(8px)',
                  // subtle themed image inside the card for artistic presentation
                  backgroundImage: theme.palette.mode === 'dark'
                    ? `linear-gradient(rgba(0,0,0,0.28), rgba(0,0,0,0.18)), url(${decImg})`
                    : `linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6)), url(${decImg})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: bgPos,
                  backgroundSize: { xs: '44px', md: '120px' },
                  '&:hover': { transform: 'translateY(-6px)', boxShadow: theme.palette.mode === 'dark' ? '0 12px 36px rgba(0,0,0,0.7)' : '0 18px 40px rgba(16,24,40,0.08)' }
                })}
              >
                <Box
                  sx={theme => ({
                    minWidth: 96,
                    bgcolor: idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main,
                    color: theme.palette.getContrastText(idx % 2 === 0 ? theme.palette.primary.main : theme.palette.secondary.main),
                    fontWeight: 800,
                    fontSize: { xs: 15, sm: 16 },
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    textAlign: 'center',
                    boxShadow: 1,
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' },
                  })}
                >
                  {item.time}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, color: theme.palette.text.primary }}>
                  <Box sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 17 }, letterSpacing: 0.4, textTransform: 'none', color: theme.palette.mode === 'dark' ? '#FFF9C4' : (idx % 2 === 0 ? theme.palette.primary.dark : theme.palette.secondary.dark), mb: 0.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.activity && /\p{Emoji}/u.test(item.activity[0]) ? (
                      <span style={{ fontSize: 20, marginRight: 6 }}>{item.activity[0]}</span>
                    ) : null}
                    <span style={{ fontWeight: 800 }}>{item.activity ? item.activity.replace(/^\p{Emoji}/u, '').split(' ')[0] : ''}</span>
                    {item.speaker ? <Typography component="span" sx={{ fontWeight: 700, color: 'text.secondary', ml: 1 }}>— {item.speaker}</Typography> : null}
                  </Box>
                  <Box sx={{ fontSize: { xs: 13.5, sm: 15 }, color: 'text.secondary', fontWeight: 400, wordBreak: 'break-word', pl: 0.5 }}>
                    {item.activity ? item.activity.replace(/^\p{Emoji}/u, '').split(' ').slice(1).join(' ') : ''}
                  </Box>
                </Box>
                {/* decorative image is now used as the card background (left/right) */}
              </Box>
            );
            })}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No schedule available at this time.
          </Typography>
        )}
        <Box sx={{ width: '100%', minHeight: 120, mt: 3, mb: 2 }}>
          <img
            src="/assets/schedule.jpg"
            alt="Schedule"
            style={{ width: '100%', borderRadius: 8, boxShadow: 2, marginTop: 0 }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </Box>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="contained" color="primary" size="large" onClick={() => setOpen(true)}>
            Book Now
          </Button>
        </Box>
          <BookingModal open={open} onClose={() => setOpen(false)} />
        </Box>
        </Box>
      </Box>
    </PublicLayout>
  );
}

export default SchedulePage;
